import React from 'react';
import { useTranslation } from '../hooks/useTranslation';
import EditableText from './EditableText';
import EditableImage from './EditableImage';

const AboutSection = () => {
  const { t } = useTranslation();

  return (
    <section id="about" className="py-20 lg:py-28 bg-slate-50 border-y border-slate-200/60">
      <div className="relative z-10 container mx-auto px-6 max-w-7xl">
        <div className="text-center mb-16">
          <EditableText
            section="about"
            field="title"
            as="h2"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6"
          >
            {t('about.title')}
          </EditableText>
          <EditableText
            section="about"
            field="description"
            as="p"
            className="text-lg sm:text-xl text-slate-600 max-w-4xl mx-auto leading-relaxed"
          >
            {t('about.description')}
          </EditableText>
        </div>

        {/* 3 Feature Cards - Balanced Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          <div className="bg-white rounded-xl p-6 sm:p-8 border border-slate-200 hover:border-blue-300 shadow-sm hover:shadow-md transition-all duration-300" data-testid="about-card-1">
            <div className="w-12 h-12 bg-blue-600/10 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <EditableText 
              section="about" 
              field="feature1Title" 
              className="text-xl font-bold text-slate-900 mb-3"
            >
              {t('about.feature1Title')}
            </EditableText>
            <EditableText 
              section="about" 
              field="feature1Description" 
              className="text-slate-600 leading-relaxed"
            >
              {t('about.feature1Description')}
            </EditableText>
          </div>
          
          <div className="bg-white rounded-xl p-6 sm:p-8 border border-slate-200 hover:border-blue-300 shadow-sm hover:shadow-md transition-all duration-300" data-testid="about-card-2">
            <div className="w-12 h-12 bg-blue-600/10 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
              </svg>
            </div>
            <EditableText 
              section="about" 
              field="feature2Title" 
              className="text-xl font-bold text-slate-900 mb-3"
            >
              {t('about.feature2Title')}
            </EditableText>
            <EditableText 
              section="about" 
              field="feature2Description" 
              className="text-slate-600 leading-relaxed"
            >
              {t('about.feature2Description')}
            </EditableText>
          </div>
          
          <div className="bg-white rounded-xl p-6 sm:p-8 border border-slate-200 hover:border-blue-300 shadow-sm hover:shadow-md transition-all duration-300" data-testid="about-card-3">
            <div className="w-12 h-12 bg-blue-600/10 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <EditableText 
              section="about" 
              field="feature3Title" 
              className="text-xl font-bold text-slate-900 mb-3"
            >
              {t('about.feature3Title')}
            </EditableText>
            <EditableText 
              section="about" 
              field="feature3Description" 
              className="text-slate-600 leading-relaxed"
            >
              {t('about.feature3Description')}
            </EditableText>
          </div>
        </div>

        {/* Company Image */}
        <div className="flex justify-center">
          <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm max-w-2xl">
            <EditableImage
              section="about"
              field="companyImage"
              src="https://static.prod-images.emergentagent.com/jobs/1e377d72-35c9-4698-8603-668ab4051cd7/images/d7c271d1315a964aedaf62553e60e1e4defbf9d41308d201f93a23896fe1df61.jpeg"
              alt="International Trade - China Port Logistics"
              className="w-full h-64 object-cover rounded-lg"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;